import React, { useCallback, useMemo, useState } from 'react'

import moment from 'moment'
import { func, number, object } from 'prop-types'
import { Alert, Checkbox, Table, Tag } from 'antd'
import { downloadCSV } from '../../../utils'
import { InternshipImportModes } from '../../../utils/api/internship'

import '../../../assets/import-report.scss'

export const ImportStatuses = Object.freeze({
  CREATED: 'CREATED',
  ERROR: 'ERROR'
})

const STATUS_COLORS = Object.freeze({
  [ImportStatuses.CREATED]: 'green',
  [ImportStatuses.ERROR]: 'red'
})

const COUNTERS = Object.freeze([
  { key: 'created', color: 'green' },
  { key: 'errors', color: 'red' },
  { key: 'warnings', color: 'gold', subset: true }
])

const PAGE_SIZE = 50
const ROW_HEIGHT = 39
const MIN_BODY_HEIGHT = 120
const CHROME_HEIGHT = 460

const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/
const ISO_DATE = 'YYYY-MM-DD'
const SHOWN_DATE = 'DD/MM/YYYY'
const EMPTY_VALUE = '—'

export const translateCode = (code, t) => {
  const [key, ...rest] = String(code).split(':')
  const detail = rest.join(':')

  if (!CODE_PATTERN.test(key)) {
    return t('internships_import.errors.UNEXPECTED')
  }

  return t(`internships_import.errors.${key}`, { detail })
}

const codes = value => typeof value === 'string' && value !== '' ? value.split(', ').filter(code => code !== '') : []

const printedDate = value => {
  if (!value) {
    return EMPTY_VALUE
  }

  const date = moment(value, ISO_DATE, true)

  return date.isValid() ? date.format(SHOWN_DATE) : value
}

const printed = value => value === null || value === undefined || value === '' ? EMPTY_VALUE : value

const reasonsText = (item, t) => [...codes(item.errors), ...codes(item.warnings)].map(code => translateCode(code, t)).join(' · ')

const InternshipsImportReport = ({ height, report, t }) => {
  const isPreview = report.mode === InternshipImportModes.DRY_RUN
  const summary = report.summary

  const [errorsOnly, setErrorsOnly] = useState(false)

  const rows = useMemo(
    () => errorsOnly ? report.data.filter(item => item.status === ImportStatuses.ERROR) : report.data,
    [errorsOnly, report.data]
  )

  const bodyHeight = useMemo(() => Math.max(
    MIN_BODY_HEIGHT,
    Math.min(Math.min(rows.length, PAGE_SIZE) * ROW_HEIGHT, height - CHROME_HEIGHT)
  ), [height, rows.length])

  const statusLabel = useCallback(
    status => t(`internships_import.status.${isPreview ? 'preview' : 'done'}.${status}`),
    [isPreview, t]
  )

  const columns = useMemo(() => [
    {
      title: t('internships_import.report.line'),
      dataIndex: 'line',
      width: 70
    },
    {
      title: t('internships_import.report.status'),
      dataIndex: 'status',
      width: 140,
      render: status => <Tag color={STATUS_COLORS[status]}>{statusLabel(status)}</Tag>
    },
    {
      title: t('internships_import.report.student'),
      key: 'student',
      render: (value, item) => printed(item.internship.student_email)
    },
    {
      title: t('internships_import.report.sector'),
      key: 'sector',
      render: (value, item) => (
        <div className='import-sector'>
          <span className='name'>{printed(item.internship.sector_name ?? item.internship.sector_id)}</span>
          {item.internship.institution_name && <span className='institution'>{item.internship.institution_name}</span>}
        </div>
      )
    },
    {
      title: t('internships_import.report.dates'),
      key: 'dates',
      width: 190,
      render: (value, item) => `${printedDate(item.internship.start_date)} → ${printedDate(item.internship.end_date)}`
    },
    {
      title: t('internships_import.report.reasons'),
      key: 'reasons',
      render: (value, item) => (
        <ul className='import-reasons'>
          {codes(item.errors).map(code => <li key={code} className='error'>{translateCode(code, t)}</li>)}
          {codes(item.warnings).map(code => <li key={code} className='warning'>{translateCode(code, t)}</li>)}
        </ul>
      )
    }
  ], [statusLabel, t])

  const exportReport = useCallback(() => {
    downloadCSV(report.data.map(item => ({
      [t('internships_import.report.line')]: item.line,
      [t('internships_import.report.status')]: statusLabel(item.status),
      student_email: item.internship.student_email,
      start_date: item.internship.start_date,
      end_date: item.internship.end_date,
      sector_id: item.internship.sector_id,
      [t('internships_import.report.sector')]: item.internship.sector_name ?? '',
      [t('internships_import.report.institution')]: item.internship.institution_name ?? '',
      evaluation_link: item.internship.evaluation_link,
      [t('internships_import.report.reasons')]: reasonsText(item, t)
    })))
  }, [report.data, statusLabel, t])

  return (
    <div className='import-report'>
      <Alert
        type={isPreview ? 'info' : (summary.errors > 0 ? 'warning' : 'success')}
        message={t(isPreview ? 'internships_import.preview.headline' : 'internships_import.done.headline')}
        showIcon
        style={{ marginBottom: '12px' }}
      />
      <div className='import-total'>{t('internships_import.summary.total', { count: summary.total })}</div>
      <div className='import-counters'>
        {COUNTERS.map(counter => (
          <div
            key={counter.key}
            className={`import-counter ${counter.color}${counter.subset ? ' subset' : ''}${summary[counter.key] > 0 ? '' : ' empty'}`}
          >
            <div className='value'>{summary[counter.key]}</div>
            <div className='label'>{t(`internships_import.summary.${isPreview && counter.key === 'created' ? 'to_create' : counter.key}`)}</div>
          </div>
        ))}
      </div>
      {summary.errors > 0 && (
        <Checkbox
          checked={errorsOnly}
          onChange={event => setErrorsOnly(event.target.checked)}
          style={{ marginBottom: '8px' }}
        >
          {t('internships_import.report.errors_only')}
        </Checkbox>
      )}
      <Table
        columns={columns}
        dataSource={rows}
        rowKey='line'
        size='small'
        pagination={{ pageSize: PAGE_SIZE, size: 'small' }}
        scroll={{ y: bodyHeight }}
      />
      <a onClick={exportReport}>{t('internships_import.report.export')}</a>
    </div>
  )
}

InternshipsImportReport.propTypes = {
  height: number.isRequired,
  report: object.isRequired,
  t: func.isRequired
}

export default InternshipsImportReport
